import type { Params } from './types';
import { defaultParams } from './params';

export type PresetId = 'logo-bw' | 'logo-color' | 'text' | 'sketch' | 'photo' | 'icon';

export interface Preset {
  id: PresetId;
  label: string;
  patch: Partial<Params>;
}

export const presets: Preset[] = [
  {
    id: 'logo-bw',
    label: 'Logo — Clean B&W',
    patch: { mode: 'bw', thresholdMethod: 'otsu', smoothness: 1.0, speckle: 4, background: 'remove-white', opticurve: true, svgo: 'safe' },
  },
  {
    id: 'logo-color',
    label: 'Logo — Color',
    patch: { mode: 'color', colorCount: 4, curveMode: 'spline', layerDifference: 24, smoothness: 1.0, svgo: 'safe' },
  },
  {
    id: 'text',
    label: 'Text / Title',
    patch: { mode: 'bw', thresholdMethod: 'otsu', smoothness: 0.6, speckle: 0, opttolerance: 0.1 },
  },
  {
    id: 'sketch',
    label: 'Sketch / Line art',
    patch: { mode: 'bw', thresholdMethod: 'manual', thresholdManual: 180, smoothness: 1.2, speckle: 2, contrast: 20 },
  },
  {
    id: 'photo',
    label: 'Photo — Stylized',
    patch: { mode: 'color', colorCount: 8, posterize: 6, blur: 1, layerDifference: 16 },
  },
  {
    id: 'icon',
    label: 'Icon / Pixel art',
    patch: { mode: 'color', colorCount: 8, curveMode: 'pixel', blur: 0, smoothness: 0 },
  },
];

export function applyPreset(id: PresetId, base: Params): Params {
  const preset = presets.find((p) => p.id === id);
  if (!preset) throw new Error(`Unknown preset: ${id}`);
  return { ...base, ...preset.patch };
}

export function matchPreset(params: Params): PresetId | null {
  for (const preset of presets) {
    const baseline = { ...defaultParams(), ...preset.patch };
    if (paramsEqualForPreset(params, baseline)) return preset.id;
  }
  return null;
}

function paramsEqualForPreset(a: Params, b: Params): boolean {
  return (Object.keys(b) as (keyof Params)[]).every((k) => Object.is(a[k], b[k]));
}
